import { format } from 'date-fns';
import moment from 'moment-timezone';
import { UserFeedbackSurvey } from '../../models/survey';
import { PmStatus } from '../../api/types';

export const getUserPhase = (currentPhase) => {
  switch (currentPhase) {
    case 'PhaseOne':
      return 1;
    case 'PhaseTwo':
      return 2;
    case 'PhaseThree':
      return 3;
    case 'PhaseFour':
      return 4;
    case 'ProgramComplete':
      return 5;
    default:
      return 0;
  }
};

// phase completed is one behind the current phase
// when current phase is 0, we default to 0 instead of returning -1
export const getCompletedPhaseNumber = (currentPhase) => {
  const userPhase = getUserPhase(currentPhase);
  return userPhase > 0 ? userPhase - 1 : 0;
};

export const generateDateStringByTimestamp = (timestamp) => {
  const dateObj = timestamp ? new Date(timestamp) : null;
  return dateObj ? format(dateObj, 'MM-dd-yyyy') : null;
};

// returns first meeting where status is not set yet OR next meeting
export const computeMeetingToDisplay = <
  T extends { meeting_date: string; pm_status?: PmStatus }
>(
  meetings: T[]
) => {
  const now = new Date();
  const upperLimit = new Date(now.getTime() + 96 * 60 * 60 * 1000); // 96 hours

  const meetingsWithin96Hours = meetings
    .filter((meeting: T) => {
      const date = new Date(meeting.meeting_date);

      // include future (up to 4 days) and past meetings which don't have a status
      return date <= upperLimit && !meeting.pm_status;
    })
    .sort(
      (a: T, b: T) =>
        new Date(a.meeting_date).getTime() - new Date(b.meeting_date).getTime()
    );

  return meetingsWithin96Hours[0];
};

export const formatDate = (
  isoDateStr: string | undefined,
  // default formatting if not provided,
  // z stands for the timezone abbreviation
  formatPattern: string = 'MMM D, h:mm A z'
): string => {
  if (!isoDateStr) return '--';

  return moment.parseZone(isoDateStr).format(formatPattern);
};

export const formatDateInTZ = (
  isoDateStr: string | undefined,
  timezone: string | undefined = 'America/New_York',
  formatPattern: string = 'dddd, MMMM D, h:mm A z'
) => {
  if (!isoDateStr) return '--';

  return moment
    .parseZone(isoDateStr)
    .tz(timezone)
    ?.format(formatPattern);
};

export const formatDateInBrowserTZ = (
  isoDateStr: string | undefined,
  // default formatting if not provided,
  // z stands for the timezone abbreviation
  formatPattern: string = 'MMM D, h:mm A z'
): string => {
  if (!isoDateStr) return '--';

  // Automatically get browser's TZ
  const browserTimeZone = moment.tz.guess();
  const localTime = moment.tz(isoDateStr, browserTimeZone);

  return localTime.format(formatPattern);
};

export const getMeetingGoalsByMeetingType = (
  surveys: UserFeedbackSurvey[],
  meetingType: string
) => {
  return surveys.find((s) => s.survey?.appointment_type === meetingType)?.survey
    ?.goals;
};
